import { isPlatformBrowser } from '@angular/common';
import {
  computed,
  effect,
  inject,
  Injectable,
  OnDestroy,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, switchMap, timer } from 'rxjs';
import { AuthService } from '../../core/services/auth/auth.service';
import { ToastService } from '../../core/services/toast/toast.service';
import { CacheService } from '../../ssr-services/cache/cache.service';
import { SubscriberService } from '../../ssr-services/subscriber/subscriber.service';
import { IdentityVerificationService } from '../identity-verification/identity-verification.service';
import { SocketResponse } from '../payment-websocket/payment-websocket.service';
import { SimsService } from '../sims/sims.service';

export interface PaymentSuccessSubscriptionData {
  id?: string;
  status?: string;
  data?: string;
  invoiceHash?: string | null;
  activationCode?: string;
  iccid?: string;
  transactionId?: string;
  esimName?: string;
  planId?: string;
  paymentId?: string;
}

export interface WebSocketMessage {
  customerId?: string;
  subscriberId?: string;
  status?: string;
  sessionId?: string;
  sessionToken?: string;
  sessionUrl?: string;
  message?: string;
  event?: string;
  eventType?: string;
  payload?: {
    status?: string;
    message?: string;
    sessionId?: string;
    sessionToken?: string;
    sessionUrl?: string;
    subscriptionData?: PaymentSuccessSubscriptionData;
    subscriptionId?: string;
    esimId?: string;
    transactionId?: string;
  };
  notificationType?: string;
  subscriptionId?: string;
  webSocketMessage?: string;
  data?: number;
  dataUnit?: string;
  amount?: string;
  cardLast4Digits?: string;
  retryPaymentUrl?: string;
  esimId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class WebsocketService implements OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly cacheService = inject(CacheService);
  private readonly toastService = inject(ToastService);
  private readonly authService = inject(AuthService);
  private readonly identityVerificationService = inject(IdentityVerificationService);
  private readonly subscriberService = inject(SubscriberService);
  private readonly translateService = inject(TranslateService);
  private readonly simsService = inject(SimsService);

  private socket?: WebSocket;

  // Message and connection state signals
  public readonly messages = signal<WebSocketMessage | null>(null);
  public readonly isConnected = signal(false);
  public readonly isConnecting = signal(false);
  public readonly reconnectAttempts = signal(0);

  public readonly paymentSuccessEvent = signal<PaymentSuccessSubscriptionData | null>(null);
  public readonly paymentFailureEvent = signal<WebSocketMessage['payload'] | null>(null);

  // Computed signals for connection monitoring
  public readonly connectionStatus = computed(() => ({
    isConnected: this.isConnected(),
    isConnecting: this.isConnecting(),
    reconnectAttempts: this.reconnectAttempts(),
  }));

  public readonly currentStatus = computed(() => this.messages()?.status);

  // Auto Top-up execution threshold from Cache API (e.g. 80). Backend derives from dataUsageTriggers.trigger3.
  public readonly autoTopupTriggerThreshold = computed(
    () => this.cacheService.cachedContent()?.cache?.config?.topupPlansAutoDataUsageTrigger
  );

  private readonly maxReconnectDelay = 30000; // 30 seconds
  private readonly maxReconnectAttempts = 10; // Stop retrying after 10 failures to avoid infinite loop

  private manuallyClosed = false;
  private reconnectTimerSubscription?: Subscription;
  private lastProcessedEkycMessage: string | null = null;
  private lastProcessedNotificationMessage: string | null = null;
  private lastProcessedPaymentEventId: string | null = null;
  private lastProcessedSubscriberDeletedEventId: string | null = null;
  private connectionAttemptInProgress = false;
  private lastConnectedToken: string | null = null;
  private isGuestUser: boolean | null = null;

  private static readonly STATUS_EVENT_TYPES = new Set([
    'ekycStarted',
    'ekycCompleted',
    'paymentSuccess',
    'paymentFailure',
    'subscriberDeleted',
  ]);

  constructor() {
    this.initUserAndConfigWatchers();
    this.initStatusWatcher();
    this.initDataUsageAndAutoTopupWatcher();
  }

  /**
   * Watches auth, subscriber, and config. Connects after sign-in when a real
   * subscriber is loaded; disconnects on logout so the next login opens a fresh socket.
   */
  private initUserAndConfigWatchers(): void {
    effect(() => {
      const subscriber = this.subscriberService.subscriber();
      const cache = this.cacheService.cachedContent();
      const isAuthenticated = this.authService.isAuthenticated();
      const isGuestUser = !isAuthenticated;
      const token = this.authService.token()?.token ?? null;

      if (!isPlatformBrowser(this.platformId)) {
        return;
      }

      // Guest and logged-in transition: reconnect so socket token type switches.
      if (this.isGuestUser === null) {
        this.isGuestUser = isGuestUser;
      } else if (this.isGuestUser !== isGuestUser) {
        this.isGuestUser = isGuestUser;
        this.reconnect();
        return;
      }

      if (!cache?.cache?.config) {
        return;
      }

      const webSocketUrl = cache.cache.config.webSocketUrl;
      if (!webSocketUrl) {
        return;
      }

      if (isAuthenticated) {
        if (!token || !subscriber || subscriber._id === '-1') {
          return;
        }
        if (
          this.socket?.readyState === WebSocket.OPEN &&
          this.lastConnectedToken &&
          this.lastConnectedToken !== token
        ) {
          this.reconnect();
          return;
        }
      }

      if (this.connectionAttemptInProgress || this.isConnected() || this.isConnecting()) {
        return;
      }

      setTimeout(() => {
        if (!this.connectionAttemptInProgress) {
          this.tryInitializeConnection();
        }
      }, 100);
    });
  }

  /**
   * Watches WebSocket messages and dispatches by eventType (or legacy status/session for eKYC).
   */
  private initStatusWatcher(): void {
    effect(() => {
      const data = this.messages();
      if (!data) return;
      this.dispatchStatusWatcherMessage(data);
    });
  }

  private dispatchStatusWatcherMessage(data: WebSocketMessage): void {
    if (
      this.tryDispatchEkycEventMessages(data) ||
      this.tryDispatchPaymentOrSubscriberEventMessages(data)
    ) {
      return;
    }
    this.dispatchLegacyEkycIfApplicable(data);
  }

  private tryDispatchEkycEventMessages(data: WebSocketMessage): boolean {
    const eventType = data.eventType;
    if (eventType === 'ekycStarted') {
      this.handleEkycEvent(data, data.payload?.status ?? data.status ?? 'ongoing');
      return true;
    }
    if (eventType === 'ekycCompleted') {
      this.handleEkycEvent(data, data.payload?.status ?? data.status ?? 'success');
      this.showEkycCompletedToast(data.payload?.status ?? data.status);
      return true;
    }
    return false;
  }

  private tryDispatchPaymentOrSubscriberEventMessages(data: WebSocketMessage): boolean {
    const eventType = data.eventType;
    if (eventType === 'paymentSuccess') {
      if (!this.tryProcessPaymentSuccessEvent(data)) return true;
      this.handlePaymentSuccessEvent(data);
      return true;
    }
    if (eventType === 'paymentFailure') {
      if (!this.tryProcessPaymentFailureEvent(data)) return true;
      this.handlePaymentFailureEvent(data);
      return true;
    }
    if (eventType === 'subscriberDeleted') {
      if (!this.tryProcessSubscriberDeletedEvent(data)) return true;
      this.handleSubscriberDeleted();
      return true;
    }
    return false;
  }

  /** Legacy: eKYC messages identified by status + sessionId/sessionToken (no eventType). */
  private dispatchLegacyEkycIfApplicable(data: WebSocketMessage): void {
    const status = this.currentStatus();
    if (!status) return;

    const sessionId = data.payload?.sessionId ?? data.sessionId;
    const sessionToken = data.payload?.sessionToken ?? data.sessionToken;
    if (!sessionId && !sessionToken) return;

    const messageId = `${data.status}-${sessionId}-${sessionToken}`;
    if (this.lastProcessedEkycMessage === messageId) return;
    this.lastProcessedEkycMessage = messageId;

    const ekycStatus = data.status || '';
    this.updateEkycDetailsAndSubscriber(data, ekycStatus);
    this.showEkycCompletedToast(ekycStatus);
  }

  private handleEkycEvent(data: WebSocketMessage, status: string): void {
    const messageId = `ekyc-${data.eventType}-${data.payload?.sessionId ?? data.sessionId}-${data.payload?.sessionToken ?? data.sessionToken}`;
    if (this.lastProcessedEkycMessage === messageId) return;
    this.lastProcessedEkycMessage = messageId;
    this.updateEkycDetailsAndSubscriber(data, status);
  }

  private updateEkycDetailsAndSubscriber(data: WebSocketMessage, ekycStatus: string): void {
    const normalizedStatus = this.normalizeEkycStatus(ekycStatus);
    const sessionId = data.payload?.sessionId ?? data.sessionId ?? '';
    const sessionToken = data.payload?.sessionToken ?? data.sessionToken ?? '';
    const sessionUrl = data.payload?.sessionUrl ?? data.sessionUrl ?? '';

    this.identityVerificationService.updateEkycDetails({
      status: normalizedStatus,
      sessionId,
      sessionToken,
      sessionUrl,
    });

    const currentSubscriber = this.subscriberService.subscriber();
    if (currentSubscriber) {
      this.subscriberService.subscriber.set({
        ...currentSubscriber,
        eKycStatus: normalizedStatus,
      });
    }
  }

  private normalizeEkycStatus(status: string): string {
    const sanitizedStatus = status.trim().toLowerCase();

    if (!sanitizedStatus) return 'ongoing';
    if (sanitizedStatus === 'started' || sanitizedStatus === 'initiated') return 'ongoing';
    if (sanitizedStatus === 'completed' || sanitizedStatus === 'verified') return 'success';
    return sanitizedStatus;
  }

  private showEkycCompletedToast(status: string | undefined): void {
    if (status === 'failed' || status === 'rejected') {
      const message = this.translateService.instant('identity_verification.verification_rejected');
      this.toastService
        .openErrorToast(message)
        .pipe(switchMap((toast) => toast.present()))
        .subscribe();
    }
  }

  private static readonly DATA_USAGE_TYPES = new Set(['dataUsage50', 'dataUsage70', 'dataUsage80']);
  private static readonly DATA_USAGE_TOPUP_TYPES = new Set([
    'dataUsageTopup50',
    'dataUsageTopup70',
    'dataUsageTopup80',
  ]);
  private static readonly DATA_USAGE_AUTO_TOPUP_TYPES = new Set([
    'dataUsageAutoTopup50',
    'dataUsageAutoTopup70',
    'dataUsageAutoTopup',
  ]);
  private static readonly SUBSCRIPTION_EXPIRY_TYPES = new Set([
    'subscriptionExpiryTopup',
    'subscriptionExpiryReminder',
  ]);

  /**
   * Watches for data usage, auto top-up, and subscription expiry notifications.
   * Data usage: trigger1/2 reminder, trigger3 Auto Top-Up; success/failure toasts.
   * Subscription expiry: Auto Top-Up 1h before (config) when enabled; reminder 24h before (config) when not.
   */
  private initDataUsageAndAutoTopupWatcher(): void {
    effect(() => {
      const data = this.messages();
      if (!data?.notificationType) return;
      if (data.eventType && WebsocketService.STATUS_EVENT_TYPES.has(data.eventType)) return;

      const messageId = `${data.notificationType}-${data.subscriptionId}-${data.webSocketMessage}`;
      if (this.lastProcessedNotificationMessage === messageId) return;
      this.lastProcessedNotificationMessage = messageId;

      this.dispatchNotification(data);
    });
  }

  private dispatchNotification(data: WebSocketMessage): void {
    const msg = data.webSocketMessage ?? '';
    const type = data.notificationType!;

    if (WebsocketService.DATA_USAGE_TYPES.has(type)) {
      this.showDataUsageToast(
        msg,
        "You've used a significant amount of your data. Check your plan details."
      );
      return;
    }
    if (WebsocketService.DATA_USAGE_TOPUP_TYPES.has(type)) {
      this.showDataUsageToast(msg, 'Your data is running low. Top up now to avoid interruption.');
      return;
    }
    if (WebsocketService.DATA_USAGE_AUTO_TOPUP_TYPES.has(type)) {
      this.handleDataUsageAutoTopup(msg);
      return;
    }
    if (type === 'autoTopupSuccess') {
      this.handleAutoTopupSuccess(data, msg);
      return;
    }
    if (type === 'autoTopupFailed') {
      this.handleAutoTopupFailed(data.webSocketMessage || undefined);
      return;
    }
    if (WebsocketService.SUBSCRIPTION_EXPIRY_TYPES.has(type)) {
      this.handleSubscriptionExpiryReminder(data.webSocketMessage || undefined);
      return;
    }
    if (type === 'subscriptionExpiryAutoTopupSuccess') {
      this.handleAutoTopupSuccess(data, data.webSocketMessage ?? undefined);
    }
  }

  private handleDataUsageAutoTopup(webSocketMessage: string): void {
    const threshold = this.autoTopupTriggerThreshold() ?? 80;
    let message =
      webSocketMessage || `Auto Top-Up will be initiated at ${threshold}% data consumption`;
    const configThreshold = this.autoTopupTriggerThreshold();
    if (configThreshold && message.includes('{{triggerThreshold}}')) {
      message = message.replace('{{triggerThreshold}}', String(configThreshold));
    }
    this.showSuccessToast(message);
  }

  private handleAutoTopupSuccess(data: WebSocketMessage, webSocketMessage?: string): void {
    const defaultMsg =
      '{{data}} {{dataUnit}} added successfully. {{amount}} has been charged from your card ending with {{cardLast4Digits}}.';
    const message = this.replaceAutoTopupTemplateVars(webSocketMessage || defaultMsg, data);
    this.showSuccessToast(message);
    this.simsService.fetchSims().subscribe();
  }

  private handleAutoTopupFailed(
    webSocketMessage: string = 'Auto Top-Up failed. Please update your payment method.'
  ): void {
    this.toastService
      .openErrorToast(webSocketMessage)
      .pipe(switchMap((toast) => toast.present()))
      .subscribe();
  }

  private handleSubscriptionExpiryReminder(
    webSocketMessage: string = 'Your plan will be expired in 1 day– top up now to stay online!'
  ): void {
    let message = webSocketMessage;
    const reminderHours =
      this.cacheService.cachedContent()?.cache?.config?.subscriptionExpiryReminder;
    if (reminderHours != null && message.includes('{{hours}}')) {
      message = message.replace('{{hours}}', String(reminderHours));
    }
    this.showSuccessToast(message);
  }

  private replaceAutoTopupTemplateVars(template: string, data: WebSocketMessage): string {
    let message = template;
    if (data.data != null && data.dataUnit) {
      message = message.replace('{{data}}', String(data.data));
      message = message.replace('{{dataUnit}}', data.dataUnit);
    }
    if (data.amount) message = message.replace('{{amount}}', data.amount);
    if (data.cardLast4Digits) {
      message = message.replace('{{cardLast4Digits}}', data.cardLast4Digits);
    }
    return message;
  }

  private showSuccessToast(message: string): void {
    this.toastService
      .openSuccessToast(message)
      .pipe(switchMap((toast) => toast.present()))
      .subscribe();
  }

  private showDataUsageToast(webSocketMessage: string, defaultMessage: string): void {
    const message = webSocketMessage || defaultMessage;
    if (message) {
      this.toastService
        .openSuccessToast(message)
        .pipe(switchMap((toast) => toast.present()))
        .subscribe();
    }
  }

  /**
   * Attempts to initialize the WebSocket connection if running in browser.
   */
  private tryInitializeConnection(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Prevent multiple connection attempts
    if (this.isConnected() || this.isConnecting() || this.connectionAttemptInProgress) {
      return;
    }

    this.connectionAttemptInProgress = true;

    const subscriber = this.subscriberService.subscriber();
    const cache = this.cacheService.cachedContent();
    const customerId = cache?.cache?.customerId;
    const webSocketUrl = cache?.cache?.config?.webSocketUrl;
    const isAuthenticated = this.authService.isAuthenticated();

    if (!webSocketUrl) {
      this.connectionAttemptInProgress = false;
      return;
    }

    if (!isAuthenticated) {
      if (!customerId) {
        this.connectionAttemptInProgress = false;
        return;
      }
      // Guest flow: connect with guest JWT tied to stable per-device id.
      const socketDeviceId = this.getSocketDeviceId();
      const guestToken = this.generateGuestJwt(customerId, socketDeviceId);
      this.connect(guestToken, webSocketUrl);
      return;
    }

    if (!subscriber || subscriber._id === '-1') {
      this.connectionAttemptInProgress = false;
      return;
    }

    if (this.authService.hasRefreshTokenExpired()) {
      this.connectionAttemptInProgress = false;
      return;
    }

    if (!this.authService.hasTokenExpired()) {
      this.connectWithCurrentToken(webSocketUrl);
      return;
    }

    this.authService.fetchRefreshToken().subscribe({
      next: (result) => {
        if (!result) {
          this.connectionAttemptInProgress = false;
          return;
        }
        this.connectWithCurrentToken(webSocketUrl);
      },
      error: () => {
        this.connectionAttemptInProgress = false;
      },
    });
  }

  /** Opens the socket using the current JWT (after optional refresh). */
  private connectWithCurrentToken(webSocketUrl: string): void {
    const tokenString = this.authService.token()?.token;
    if (!tokenString) {
      this.connectionAttemptInProgress = false;
      return;
    }
    this.connect(tokenString, webSocketUrl);
  }

  /** Stable browser-local id used to correlate guest checkout with guest websocket updates. */
  private getSocketDeviceId(): string {
    if (!isPlatformBrowser(this.platformId)) return '';

    const key = 'socketDeviceId';
    const existing = globalThis.localStorage.getItem(key);
    if (existing) return existing;

    const generated = globalThis.crypto.randomUUID();
    globalThis.localStorage.setItem(key, generated);
    return generated;
  }

  /** Generate guest JWT token. */
  private generateGuestJwt(customerId: string, socketDeviceId: string): string {
    const header = { alg: 'none', typ: 'JWT' };
    const payload = {
      tokenType: 'guestToken',
      user: { customerid: customerId },
      socketDeviceId,
    };
    return `${this.base64UrlEncode(header)}.${this.base64UrlEncode(payload)}.`;
  }

  private base64UrlEncode(value: object): string {
    const json = JSON.stringify(value);
    return globalThis.btoa(json).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/g, '');
  }

  /**
   * Opens a WebSocket connection with the given token and sets subprotocols.
   */
  private connect(token: string, wsUrl: string): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      if (this.lastConnectedToken === token) {
        this.connectionAttemptInProgress = false;
        this.isConnecting.set(false);
        this.isConnected.set(true);
        return;
      }
      this.socket.close(1000, 'Token changed');
      this.socket = undefined;
    }

    this.manuallyClosed = false;
    this.lastConnectedToken = token;
    this.isConnecting.set(true);
    this.isConnected.set(false);

    const protocols = ['Token', token];

    try {
      this.socket = new WebSocket(wsUrl, protocols);

      this.socket.onopen = () => {
        console.log('WebSocket connected successfully!');
        this.reconnectAttempts.set(0);
        this.isConnecting.set(false);
        this.isConnected.set(true);
        this.connectionAttemptInProgress = false;
      };

      this.socket.onmessage = (event) => {
        try {
          if (typeof event.data !== 'string') {
            return;
          }
          const raw: Record<string, unknown> = JSON.parse(event.data);
          this.messages.set(WebsocketService.normalizeInboundMessage(raw));
        } catch {
          // Malformed WebSocket message - ignore to prevent unhandled rejection
        }
      };

      this.socket.onclose = () => {
        this.isConnecting.set(false);
        this.isConnected.set(false);
        this.connectionAttemptInProgress = false;

        if (!this.manuallyClosed) {
          this.scheduleReconnect();
        }
      };

      this.socket.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.isConnecting.set(false);
        this.isConnected.set(false);
        this.connectionAttemptInProgress = false;

        if (!this.manuallyClosed) {
          this.scheduleReconnect();
        }
      };
    } catch (e) {
      console.error('WebSocket connection failed:', e);
      this.isConnecting.set(false);
      this.isConnected.set(false);
      this.connectionAttemptInProgress = false;
      this.scheduleReconnect();
    }
  }

  /**
   * Schedules a reconnection attempt. Stops after maxReconnectAttempts to avoid infinite failure loop.
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimerSubscription) return;

    const currentAttempts = this.reconnectAttempts();
    if (currentAttempts >= this.maxReconnectAttempts) return;

    this.reconnectAttempts.set(currentAttempts + 1);
    const delay = this.maxReconnectDelay;

    this.reconnectTimerSubscription = timer(delay).subscribe(() => {
      this.reconnectTimerSubscription = undefined;
      if (!this.manuallyClosed) {
        this.tryInitializeConnection();
      }
    });
  }

  /**
   * Manually closes the WebSocket connection and prevents automatic reconnection.
   */
  public disconnect(): void {
    this.manuallyClosed = true;
    this.isConnecting.set(false);
    this.isConnected.set(false);

    this.reconnectTimerSubscription?.unsubscribe();
    this.reconnectTimerSubscription = undefined;

    if (this.socket) {
      this.socket.close(1000, 'Manual disconnect');
      this.socket = undefined;
    }

    this.lastConnectedToken = null;
  }

  /**
   * Manually triggers a connection attempt.
   */
  public reconnect(): void {
    this.disconnect();
    setTimeout(() => {
      this.manuallyClosed = false;
      this.reconnectAttempts.set(0);
      this.tryInitializeConnection();
    }, 100);
  }

  /**
   * Gets the current connection status.
   */
  public getConnectionStatus() {
    return this.connectionStatus();
  }

  /** Clears payment success/failure signals (e.g. when leaving purchase-success). */
  public clearPaymentEvents(): void {
    this.paymentSuccessEvent.set(null);
    this.paymentFailureEvent.set(null);
  }

  /** Maps central WebSocket payment payload to the legacy SocketResponse shape used by checkout UI. */
  public static toSocketResponse(data: PaymentSuccessSubscriptionData): SocketResponse {
    return {
      success: true,
      data: data.id ?? data.data,
      invoiceHash: data.invoiceHash ?? undefined,
      activationCode: data.activationCode,
      iccid: data.iccid,
      transactionId: data.transactionId,
    };
  }

  private tryProcessPaymentSuccessEvent(data: WebSocketMessage): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    const sub = data?.payload?.subscriptionData;
    if (!sub) return false;
    const planOrSubId = sub.planId ?? sub.id ?? sub.paymentId ?? sub.data;
    if (!planOrSubId) return false;
    const eventId = `success-${planOrSubId}-${sub.transactionId ?? ''}`;
    if (this.lastProcessedPaymentEventId === eventId) return false;
    this.lastProcessedPaymentEventId = eventId;
    return true;
  }

  private tryProcessPaymentFailureEvent(data: WebSocketMessage): boolean {
    const payload = data?.payload;
    const eventId = `failure-${payload?.message ?? ''}-${payload?.transactionId ?? ''}`;
    if (this.lastProcessedPaymentEventId === eventId) return false;
    this.lastProcessedPaymentEventId = eventId;
    return true;
  }

  private handlePaymentSuccessEvent(data: WebSocketMessage): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!data?.payload?.subscriptionData) return;
    this.paymentFailureEvent.set(null);
    this.paymentSuccessEvent.set(data.payload.subscriptionData);
  }

  private tryProcessSubscriberDeletedEvent(data: WebSocketMessage): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    const eventId = `subscriberDeleted-${data.subscriberId ?? ''}-${data.customerId ?? ''}`;
    if (this.lastProcessedSubscriberDeletedEventId === eventId) return false;
    this.lastProcessedSubscriberDeletedEventId = eventId;
    return true;
  }

  private handleSubscriberDeleted(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.authService.isAuthenticated()) return;
    this.disconnect();
    this.authService.clearTokenState();
    void this.router.navigate(['/']);
  }

  private handlePaymentFailureEvent(data: WebSocketMessage): void {
    this.paymentSuccessEvent.set(null);
    this.paymentFailureEvent.set(data.payload ?? null);
    const message =
      data.payload?.message ??
      data.webSocketMessage ??
      data.message ??
      this.translateService.instant('payment_failed');
    this.toastService
      .openErrorToast(message)
      .pipe(switchMap((toast) => toast.present()))
      .subscribe();
  }

  /**
   * Cleanup method to be called when the service is destroyed.
   */
  public ngOnDestroy(): void {
    this.disconnect();
  }

  /** Normalizes backend envelope shapes (top-level, content.data, payload) into one message. */
  private static normalizeInboundMessage(raw: Record<string, unknown>): WebSocketMessage {
    const content = raw['content'] as { data?: Record<string, unknown> } | undefined;
    const contentData = content?.data;
    const topPayload = raw['payload'] as Record<string, unknown> | undefined;
    const nestedPayload = contentData?.['payload'] as Record<string, unknown> | undefined;
    const mergedPayload: Record<string, unknown> = {};
    if (nestedPayload != null) {
      Object.assign(mergedPayload, nestedPayload);
    }
    if (topPayload != null) {
      Object.assign(mergedPayload, topPayload);
    }
    const payload =
      Object.keys(mergedPayload).length > 0
        ? (mergedPayload as WebSocketMessage['payload'])
        : undefined;

    const str = (value: unknown): string | undefined =>
      typeof value === 'string' ? value : undefined;

    return {
      ...raw,
      eventType: str(raw['eventType']) ?? str(raw['event']),
      status: str(raw['status']) ?? str(mergedPayload['status']) ?? str(contentData?.['status']),
      sessionId:
        str(raw['sessionId']) ?? str(mergedPayload['sessionId']) ?? str(contentData?.['sessionId']),
      sessionToken:
        str(raw['sessionToken']) ??
        str(mergedPayload['sessionToken']) ??
        str(contentData?.['sessionToken']),
      sessionUrl:
        str(raw['sessionUrl']) ??
        str(mergedPayload['sessionUrl']) ??
        str(contentData?.['sessionUrl']),
      payload,
      notificationType:
        str(raw['notificationType']) ??
        str(raw['event']) ??
        str(raw['type']) ??
        str(raw['eventType']),
      webSocketMessage:
        str(raw['webSocketMessage']) ??
        str(raw['message']) ??
        str(contentData?.['message']) ??
        str(mergedPayload['message']),
      subscriptionId:
        str(raw['subscriptionId']) ??
        str(contentData?.['subscriptionId']) ??
        str(mergedPayload['subscriptionId']),
      esimId: str(raw['esimId']) ?? str(contentData?.['esimId']) ?? str(mergedPayload['esimId']),
    };
  }
}
